import * as Auth from 'aws-amplify/auth';
import { AuthError, FetchUserAttributesOutput } from 'aws-amplify/auth';
import { useCallback, useState } from 'react';
import { appConfig } from 'shared/appConfig';
import { useCreateExpertMutation } from 'shared/hooks/api/createExpert/createExpert.generated';
import {
  ExpertDataQuery,
  useExpertDataLazyQuery,
} from 'shared/hooks/api/expertData/expertData.generated';
import { useSendMagicLinkMutation } from 'shared/hooks/api/sendMagicLink/sendMagicLink.generated';
import { useAuthStore } from 'shared/hooks/stores/useAuthStore';
import { useExpertProfileStore } from 'shared/hooks/stores/useExpertProfileStore';
import { useHubStateStore } from 'shared/hooks/stores/useHubStateStore';

export enum CognitoErrorCodesEnum {
  NotAuthorizedException = 'NotAuthorizedException',
  UserNotFoundException = 'UserNotFoundException',
  UserNotConfirmedException = 'UserNotConfirmedException',
  PasswordResetRequiredException = 'PasswordResetRequiredException',
  LimitExceededException = 'LimitExceededException',
  InvalidParameterException = 'InvalidParameterException',
  UnexpectedLambdaException = 'UnexpectedLambdaException',
  UserLambdaValidationException = 'UserLambdaValidationException',
  TooManyRequestsException = 'TooManyRequestsException',
  ExpiredCodeException = 'ExpiredCodeException',
  UserAlreadyAuthenticatedException = 'UserAlreadyAuthenticatedException',
}

export enum CognitoAuthFlowEnum {
  MAGIC_LINK = 'MAGIC_LINK',
  PROVIDER = 'PROVIDER',
}

const handleCognitoError = (err: unknown): never => {
  console.error('Cognito Error:', err);
  if (typeof err === 'object' && err !== null && 'name' in err) {
    const errorName = (err as any).name;

    if (Object.values(CognitoErrorCodesEnum).includes(errorName)) {
      throw new Error(errorName);
    }

    throw new Error('Unknown Cognito Error');
  }

  throw new Error('UnknownError');
};

export const getAuthorizationToken = async () => {
  try {
    return (await Auth.fetchAuthSession()).tokens?.idToken?.toString();
  } catch (err) {
    handleCognitoError(err);
    return null;
  }
};

const prepareEmail = (email: string) => email.trim().toLowerCase();

export interface AuthState {
  user: FetchUserAttributesOutput;
  expertId: string;
  hubState: ExpertDataQuery['expertHubState'] | null;
  profileData: ExpertDataQuery['expertHubData'] | null;
}

const getAuthFlowFromUser = (user: FetchUserAttributesOutput) => {
  if (user.identities) {
    return CognitoAuthFlowEnum.PROVIDER;
  }
  return CognitoAuthFlowEnum.MAGIC_LINK;
};

export const useAuth = (initialLoading = false) => {
  const authenticateUser = useAuthStore((store) => store.authenticateUser);
  const logoutUser = useAuthStore((store) => store.logoutUser);
  const loadExpertProfile = useExpertProfileStore((store) => store.loadExpertProfile);
  const loadHubStateData = useHubStateStore((store) => store.loadHubStateData);

  const [sendMagicLinkMutation] = useSendMagicLinkMutation();
  const [createExpert] = useCreateExpertMutation();
  const [getExpertHubState] = useExpertDataLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [loadings, setLoadings] = useState({
    verifyAuth: initialLoading,
    sendMagicLink: false,
    verifyMagicLink: false,
    signInWithGoogle: false,
    signInWithLinkedIn: false,
    signOut: false,
  });

  const fetchAuthState = useCallback(async (): Promise<AuthState | null> => {
    try {
      setLoadings((prev) => ({ ...prev, verifyAuth: true }));
      const user = await Auth.fetchUserAttributes();
      const expertId = await createExpert().then(({ data }) => data?.createExpertIdentity);
      if (!user || !expertId) {
        return null;
      }
      const expertData = await getExpertHubState().then(({ data }) => ({
        hubState: data?.expertHubState,
        profileData: data?.expertHubData,
      }));
      return {
        user,
        expertId,
        hubState: expertData.hubState ?? null,
        profileData: expertData.profileData ?? null,
      };
    } catch (err) {
      if ((err as AuthError).name !== 'UserUnAuthenticatedException') {
        handleCognitoError(err);
      }
      return null;
    } finally {
      setLoadings((prev) => ({ ...prev, verifyAuth: false }));
    }
  }, []);

  const commitAuthState = useCallback((state: AuthState) => {
    if (state.hubState) {
      loadHubStateData(state.hubState);
    }
    if (state.profileData) {
      loadExpertProfile(state.profileData, state.expertId, state.user.email || '');
    }
    authenticateUser({
      email: state.user.email || '',
      expertId: state.expertId,
      cognitoId: state.user.sub || '',
      authFlow: getAuthFlowFromUser(state.user),
    });
  }, []);

  const verifyAuthentication = useCallback(async (): Promise<FetchUserAttributesOutput | null> => {
    const state = await fetchAuthState();
    if (state) {
      commitAuthState(state);
    }
    return state?.user ?? null;
  }, [fetchAuthState, commitAuthState]);

  const sendMagicLink = useCallback(
    async (email: string, returnTo?: string): Promise<'success' | 'failed'> => {
      try {
        setLoadings((prev) => ({ ...prev, sendMagicLink: true }));
        await sendMagicLinkMutation({
          variables: {
            email: prepareEmail(email),
            returnTo,
          },
        });
        return 'success';
      } catch (err) {
        handleCognitoError(err);
        return 'failed';
      } finally {
        setLoadings((prev) => ({ ...prev, sendMagicLink: false }));
      }
    },
    [],
  );

  const handleMagicLinkVerify = useCallback(
    async (email: string, token: string): Promise<'success' | 'failed'> => {
      try {
        setLoadings((prev) => ({ ...prev, verifyMagicLink: true }));

        try {
          await Auth.signIn({
            username: prepareEmail(email),
            options: {
              authFlowType: 'CUSTOM_WITHOUT_SRP',
            },
          });
        } catch (err) {
          if ((err as any).name === CognitoErrorCodesEnum.UserAlreadyAuthenticatedException) {
            await Auth.signOut();
            await Auth.signIn({
              username: prepareEmail(email),
              options: {
                authFlowType: 'CUSTOM_WITHOUT_SRP',
              },
            });
          } else {
            throw err;
          }
        }

        const res = await Auth.confirmSignIn({
          challengeResponse: token,
        });

        if (!res.isSignedIn) {
          return 'failed';
        }

        // NOTE: we intentionally do NOT call verifyAuthentication here. The
        // caller is responsible for fetching auth state (fetchAuthState),
        // running any follow-up work, navigating to the target page, and only
        // then committing to the stores (commitAuthState). Committing earlier
        // would trigger AppRouter re-renders that recreate the router and
        // unmount the page that is still completing the magic link flow.
        return 'success';
      } catch (err) {
        handleCognitoError(err);
        return 'failed';
      } finally {
        setLoadings((prev) => ({ ...prev, verifyMagicLink: false }));
      }
    },
    [],
  );

  const signInWithGoogle = useCallback(async () => {
    try {
      setLoadings((prev) => ({ ...prev, signInWithGoogle: true }));
      await Auth.signInWithRedirect({
        provider: 'Google',
      });
      return 'success';
    } catch (err) {
      handleCognitoError(err);
      return 'failed';
    } finally {
      setLoadings((prev) => ({ ...prev, signInWithGoogle: false }));
    }
  }, []);

  const signInWithLinkedIn = useCallback(async () => {
    try {
      setLoadings((prev) => ({ ...prev, signInWithLinkedIn: true }));
      await Auth.signInWithRedirect({
        provider: {
          custom: appConfig.cognito.linkedInCustomProviderName,
        },
      });
      return 'success';
    } catch (err) {
      handleCognitoError(err);
      return 'failed';
    } finally {
      setLoadings((prev) => ({ ...prev, signInWithLinkedIn: false }));
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setLoadings((prev) => ({ ...prev, signOut: true }));
      await Auth.signOut();
      logoutUser();
      return 'success';
    } catch (err) {
      handleCognitoError(err);
      return 'failed';
    } finally {
      setLoadings((prev) => ({ ...prev, signOut: false }));
    }
  }, []);

  return {
    verifyAuthentication,
    fetchAuthState,
    commitAuthState,
    sendMagicLink,
    handleMagicLinkVerify,
    signInWithGoogle,
    signInWithLinkedIn,
    signOut,
    loadings: {
      verifyAuth: loadings.verifyAuth,
      sendMagicLink: loadings.sendMagicLink,
      verifyMagicLink: loadings.verifyMagicLink,
      signInWithGoogle: loadings.signInWithGoogle,
      signInWithLinkedIn: loadings.signInWithLinkedIn,
      signOut: loadings.signOut,
    },
  };
};
